import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Search as SearchIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ticketsAPI } from '../../../../api/tickets.api';
import TicketDetail from '../../../../components/tickets/TicketDetail';
import CreateTicketDialog from '../../../../components/tickets/CreateTicketDialog';

const statusColors = {
  abierto: 'default',
  en_diagnostico: 'primary',
  asignado_campo: 'warning',
  resuelto: 'success',
  cerrado: 'secondary',
  cancelado: 'error'
};

const priorityColors = {
  baja: 'success',
  media: 'info',
  alta: 'warning',
  critica: 'error'
};

const statusLabels = {
  abierto: 'Abierto',
  en_diagnostico: 'En Diagnóstico',
  asignado_campo: 'En Campo',
  resuelto: 'Resuelto',
  cerrado: 'Cerrado',
  cancelado: 'Cancelado'
};

export default function TicketsCliente({ customerId, customerName }) {
  console.log('🎫🎫🎫 TicketsCliente MOUNTED - customerId:', customerId, 'customerName:', customerName);
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    console.log('🎫 useEffect ejecutado - customerId:', customerId, 'tipo:', typeof customerId);
    if (customerId) {
      loadTickets();
    } else {
      console.log('⚠️ customerId es null o undefined');
    }
  }, [customerId]);

  const loadTickets = async () => {
    console.log('🚀 Iniciando carga de tickets...');
    setLoading(true);
    setError(null);
    try {
      console.log('📡 Llamando a ticketsAPI.list()...');
      const allTickets = await ticketsAPI.list({ limit: 500 });
      console.log('✅ Total tickets en sistema:', allTickets.length);
      console.log('📋 Primeros 3 tickets:', allTickets.slice(0, 3));
      console.log('🎯 Buscando tickets para customerId:', customerId);
      
      const customerIdNum = parseInt(customerId);
      console.log('🔢 customerId convertido a número:', customerIdNum);
      
      const customerTickets = allTickets.filter(t => {
        console.log(`   Comparando ticket ${t.id} (customer_id: ${t.customer_id}) con ${customerIdNum}: ${t.customer_id === customerIdNum}`);
        return t.customer_id === customerIdNum;
      });
      
      console.log('✅ Tickets encontrados para este cliente:', customerTickets.length);
      console.log('📋 Tickets del cliente:', customerTickets);
      
      setTickets(customerTickets);
    } catch (error) {
      console.error('❌ Error loading tickets:', error);
      setError('Error al cargar los tickets del cliente');
    } finally {
      setLoading(false);
      console.log('🏁 Carga de tickets finalizada');
    }
  };

  const handleViewTicket = (ticket) => {
    console.log('👁️ Ver ticket:', ticket.id);
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    console.log('🔒 Cerrando diálogo de ticket');
    setDialogOpen(false);
    setSelectedTicket(null);
    loadTickets();
  };

  const handleCreateSuccess = () => {
    console.log('✨ Ticket creado exitosamente');
    setCreateDialogOpen(false);
    loadTickets();
  };

  const getStatusChip = (status) => {
    return (
      <Chip
        label={statusLabels[status] || status}
        color={statusColors[status] || 'default'}
        size="small"
      />
    );
  };

  const getPriorityChip = (priority) => {
    return (
      <Chip
        label={priority}
        color={priorityColors[priority] || 'default'}
        size="small"
      />
    );
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = searchTerm === '' ||
      ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === '' || ticket.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Estadísticas del cliente
  const stats = {
    total: tickets.length,
    abiertos: tickets.filter(t => t.status === 'abierto').length,
    en_diagnostico: tickets.filter(t => t.status === 'en_diagnostico').length,
    en_campo: tickets.filter(t => t.status === 'asignado_campo').length,
    resueltos: tickets.filter(t => t.status === 'resuelto').length,
    cerrados: tickets.filter(t => t.status === 'cerrado').length
  };

  console.log('📊 Estadísticas calculadas:', stats);

  return (
    <Box>
      {/* Cabecera con estadísticas */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Historial de Tickets
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            size="small"
          >
            Nuevo Ticket
          </Button>
        </Box>

        {/* Tarjetas de estadísticas */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <Typography variant="caption" color="text.secondary">Total</Typography>
              <Typography variant="h6">{stats.total}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#e3f2fd' }}>
              <Typography variant="caption" color="text.secondary">Abiertos</Typography>
              <Typography variant="h6" color="primary">{stats.abiertos}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#fff3e0' }}>
              <Typography variant="caption" color="text.secondary">En Diagnóstico</Typography>
              <Typography variant="h6" color="warning.main">{stats.en_diagnostico}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#ffebee' }}>
              <Typography variant="caption" color="text.secondary">En Campo</Typography>
              <Typography variant="h6" color="error">{stats.en_campo}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#e8f5e9' }}>
              <Typography variant="caption" color="text.secondary">Resueltos</Typography>
              <Typography variant="h6" color="success.main">{stats.resueltos}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#eceff1' }}>
              <Typography variant="caption" color="text.secondary">Cerrados</Typography>
              <Typography variant="h6">{stats.cerrados}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por número o título..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="abierto">Abierto</MenuItem>
                  <MenuItem value="en_diagnostico">En Diagnóstico</MenuItem>
                  <MenuItem value="asignado_campo">En Campo</MenuItem>
                  <MenuItem value="resuelto">Resuelto</MenuItem>
                  <MenuItem value="cerrado">Cerrado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Tabla de tickets */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : filteredTickets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No hay tickets registrados para este cliente.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ mt: 2 }}
          >
            Crear primer ticket
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ticket</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Prioridad</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Técnico</TableCell>
                <TableCell>Fecha Creación</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTickets.map((ticket) => (
                <TableRow key={ticket.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {ticket.ticket_number}
                    </Typography>
                  </TableCell>
                  <TableCell>{ticket.title}</TableCell>
                  <TableCell>{getPriorityChip(ticket.priority)}</TableCell>
                  <TableCell>{getStatusChip(ticket.status)}</TableCell>
                  <TableCell>{ticket.assigned_technician_name || '-'}</TableCell>
                  <TableCell>
                    {ticket.created_at ? format(new Date(ticket.created_at), 'dd/MM/yyyy HH:mm', { locale: es }) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewTicket(ticket)}
                      title="Ver detalles"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal de detalle de ticket */}
      <TicketDetail
        open={dialogOpen}
        onClose={handleCloseDialog}
        ticket={selectedTicket}
        onUpdate={loadTickets}
      />

      {/* Modal de creación de ticket */}
      <CreateTicketDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </Box>
  );
}
